"use client"

import { useState, useEffect } from "react"
import Image, { type ImageProps } from "next/image"

interface OptimizedImageProps extends Omit<ImageProps, "onError"> {
  fallbackFormats?: string[]
  placeholderText?: string
}

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  fallbackFormats = [],
  placeholderText,
  ...props
}: OptimizedImageProps) {
  const [imageSrc, setImageSrc] = useState<string>(src as string)
  const [currentFormatIndex, setCurrentFormatIndex] = useState(-1) // -1 means using original format
  const [hasError, setHasError] = useState(false)

  // Reset state when src changes
  useEffect(() => {
    setImageSrc(src as string)
    setCurrentFormatIndex(-1)
    setHasError(false)
  }, [src])

  const handleImageError = () => {
    // If we've already tried all fallback formats, use placeholder
    if (currentFormatIndex >= fallbackFormats.length - 1 || fallbackFormats.length === 0) {
      console.error(`Failed to load image: ${imageSrc}`)

      // Try an alternative path format before giving up
      const pathParts = imageSrc.split("/")
      const fileName = pathParts[pathParts.length - 1].split(".")[0]

      if (fileName && !imageSrc.includes("/images/")) {
        const alternativePath = `/images/${fileName}.webp`
        console.log(`OptimizedImage trying alternative path: ${alternativePath}`)
        setImageSrc(alternativePath)
        return
      }

      // If the path already has /images/ but failed, try without the extension
      if (imageSrc.includes("/images/") && imageSrc.includes(".")) {
        const basePath = imageSrc.substring(0, imageSrc.lastIndexOf("."))
        const alternativePath = `${basePath}.webp`
        console.log(`OptimizedImage trying without extension: ${alternativePath}`)
        setImageSrc(alternativePath)
        return
      }

      setHasError(true)

      // Generate placeholder URL
      const placeholderWidth = typeof width === "number" ? width : 200
      const placeholderHeight = typeof height === "number" ? height : 200
      const query = encodeURIComponent(placeholderText || alt || "Image")
      const placeholderUrl = `/placeholder.svg?height=${placeholderHeight}&width=${placeholderWidth}&query=${query}`

      // Set a small timeout to avoid potential infinite loops
      setTimeout(() => {
        setImageSrc(placeholderUrl)
      }, 0)
      return
    }

    // Try next format
    const nextFormatIndex = currentFormatIndex + 1
    setCurrentFormatIndex(nextFormatIndex)

    // Get current path without extension
    const currentPath = imageSrc as string
    const basePath = currentPath.substring(0, currentPath.lastIndexOf(".")) || currentPath

    // Set new path with next format
    const nextFormat = fallbackFormats[nextFormatIndex]
    const newPath = `${basePath}.${nextFormat}`

    console.log(`Trying fallback image format: ${newPath}`)
    setImageSrc(newPath)
  }

  return (
    <Image
      src={imageSrc || "/placeholder.svg"}
      alt={alt}
      width={width}
      height={height}
      className={className}
      onError={handleImageError}
      {...props}
    />
  )
}
